/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http'
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, provideZoneChangeDetection } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { RouterModule } from '@angular/router'
import { MyProfileAddressWidgetModule } from '@myprofile-ui/myprofile-address-widget'
import { MyProfileBirthdayWidgetModule } from '@myprofile-ui/myprofile-birthday-widget'
import { LOCALISATION_BASE_URL, LOCALISATION_VERSION } from '@myprofile-ui/myprofile-localisation-api'
import { MyProfileProfilePhoneWidgetModule } from '@myprofile-ui/myprofile-profile-phone-widget'
import { PidxNotificationsModule } from '@myprofile-ui/pidx-ui-components'
import { PidxHrefClickHandlerService, WINDOW } from '@myprofile-ui/pidx-utils'
import { NgxsDispatchPluginModule } from '@ngxs-labs/dispatch-decorator'
import { NgxsModule } from '@ngxs/store'
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular'
import { environment } from '../environments/environment'
import { AppComponent } from './app.component'
import { IENoCacheHeaderInterceptor } from './interceptors/IE-no-cache-header.interceptor'
import { HttpInterceptorProvider } from './interceptors/http-error.interceptor'
import { XTRACE_ID_PREFIX, XTraceInterceptor } from './interceptors/xtrace.interceptor'
import { LayoutComponent } from './layout.component'
import { ProfileMockApiModule } from './mock-api/mock-api.module'
import { mockDataServices } from './mocks'
import { PagesModule } from './pages/pages.module'
import { ConfigService } from './services/configuration/config.service'
import { MyProfileConfigLoaderService } from './services/configuration/my-profile-config-loader.service'
import { CustomLocaleLoaderService } from './services/custom-locale-loader.service'
import { HttpErrorHandler } from './services/error-utils/http-error-handler'
import { HttpLoaderFactory } from './services/translate-loader'
import { SharedModule } from './shared.module'
import { ValidatorsModule } from './validators/validators.module'
import { CookieConsentImplementationService } from './services/cookie-consent/cookie-consent.service'
import { CookieConsentConfig } from './services/cookie-consent/cookie-consent-config'
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core'
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular'
import { Environment, ENVIRONMENT } from './services/inject-tokens/environment'
import { PidArtFooterComponent } from '@porsche-customer/pid-art-footer-angular'
import { PageTitleComponent } from './components/page-title/page-title.component'
import { NewRelicErrorHandler } from './services/error-utils/newrelic-error-handler'
import { MyProfileEmailWidgetModule } from '@myprofile-ui/myprofile-email-widget'
import { MyProfilePreferredCommunicationChannelWidgetModule } from '@myprofile-ui/myprofile-preferred-communication-channel-widget'
import { MyProfilePreferredLanguageWidgetModule } from '@myprofile-ui/myprofile-preferred-language-widget'
import { BASE_PATH } from '@myprofile-ui/pidx-utils'
import { NavigationComponent } from './components/navigation/navigation.component'

@NgModule({
  declarations: [AppComponent, LayoutComponent],
  imports: [
    BrowserModule,
    RouterModule,

    PagesModule,
    SharedModule,

    PidxNotificationsModule,
    ValidatorsModule,

    PidArtFooterComponent,
    AuthModule.forRoot(environment.oauth),

    NgxsDispatchPluginModule.forRoot(),

    MyProfileEmailWidgetModule.forRoot(environment.featureToggles.restrictGlobalUIChanges),

    MyProfilePreferredCommunicationChannelWidgetModule.forRoot(environment.featureToggles.restrictGlobalUIChanges),

    MyProfilePreferredLanguageWidgetModule.forRoot(environment.featureToggles.restrictGlobalUIChanges),

    NgxsModule.forRoot([], { developmentMode: !environment.production }),

    MyProfileAddressWidgetModule.forRoot(
      environment.publicApi.baseUrl,
      environment.publicApi.localisation,
      environment.production,
      environment.featureToggles.restrictGlobalUIChanges,
    ),
    MyProfileProfilePhoneWidgetModule.forRoot(environment.publicApi.baseUrl, environment.featureToggles.restrictGlobalUIChanges),
    MyProfileBirthdayWidgetModule.forRoot(
      environment.publicApi.baseUrl,
      environment.publicApi.localisation,
      environment.production,
      environment.featureToggles.restrictGlobalUIChanges,
    ),
    PorscheDesignSystemModule.load({ prefix: 'profile-pds' }),
    !environment.production ? ProfileMockApiModule.forRoot(mockDataServices) : [],
    PageTitleComponent,
    NavigationComponent,
  ],
  providers: [
    { provide: ENVIRONMENT, useValue: environment.production ? Environment.P : Environment.PP },
    { provide: BASE_PATH, useValue: environment.publicApi.baseUrl },
    { provide: LOCALISATION_BASE_URL, useValue: environment.publicApi.localisation },
    { provide: LOCALISATION_VERSION, useValue: environment.production ? 'RELEASE' : 'LATEST' },
    { provide: WINDOW, useValue: window },
    { provide: XTRACE_ID_PREFIX, useValue: 'PCCK-PROFILE' },
    { provide: HTTP_INTERCEPTORS, useClass: XTraceInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: IENoCacheHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: ErrorHandler, useClass: NewRelicErrorHandler },
    provideZoneChangeDetection(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, ConfigService, MyProfileConfigLoaderService],
      },
    }),
    HttpInterceptorProvider,
    HttpErrorHandler,
    PidxHrefClickHandlerService,
    CustomLocaleLoaderService,
    CookieConsentConfig,
    CookieConsentImplementationService,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
